import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { MembersDataService, MembersColumnsService } from '../../services';
import { DialogMemberFormComponent } from './member-form/member-form.component';
import { DialogMemberDetailsComponent } from './member-details/member-details.component';
import { Member, MembersResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-members',
  templateUrl: './members.component.html',
  styleUrls: ['./members.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class MembersComponent implements OnInit, OnDestroy {

  private memberAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private memberEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private memberDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public memberToDelete: Member = {};
  public members: Member[] = [];
  public member: Member = {};
  public selectedMembers: Member[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private membersDataService: MembersDataService,
    private membersColumnsService: MembersColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('MembersComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Members - Management');
    this.primengConfig.ripple = true;
    this.getMembers();
  }

  ngOnDestroy(): void {
    console.log('MembersComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getMembers(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.membersDataService.getMembers(this.parameters).subscribe((response: MembersResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.members = response.results;
        } else {
          this.members = this.members.concat(response.results);
        }
        if (response.inlineCount > this.members.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getMembers();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('MEMBERSNOTLOADED')});
    });
  }

  importMembers(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.membersDataService.addMembers(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('MEMBERSCREATED')});
                if (proceed === j) {
                  this.getMembers();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('MEMBERSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('MEMBERSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.membersDataService.updateMembers(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('MEMBERSUPDATED')});
                if (proceed === j) {
                  this.getMembers();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('MEMBERSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('MEMBERSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.membersColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('members.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedMembers() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Members?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.membersDataService.deleteMembers(this.selectedMembers).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Members Deleted', life: 3000});
            this.getMembers();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Members not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Members not deleted'});
        });
        this.selectedMembers = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.members, this.columns, 'Members');
  }

  exportExcel() {
    ToolsService.exportExcel(this.members, this.columns, 'Members');
  }

  exportCsv() {
    ToolsService.exportCsv(this.members, this.columns, 'Members');
  }

  refresh() {
    this.membersDataService.clearCache();
    this.getMembers();
  }

  memberAdd(): void {
    if (!this.navigation.getParamValue('members.addInDialog', true)) {
      this.router.navigate(['/members/add']);
      return;
    }
    this.memberAddDialog = this.dialogService.open(DialogMemberFormComponent, {
      header: '', // this.translate.instant('NEWMEMBER'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        member: {id: null},
        users: this.membersColumnsService.users,
        fitnessGoals: this.membersColumnsService.fitnessGoals,
        sports: this.membersColumnsService.sports,
      },
    });

    this.memberAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Member added'});
        this.getMembers();
      }
    });
  }

  memberEdit(member: Member): void {
    if (!this.navigation.getParamValue('members.editInDialog', true)) {
      this.router.navigate(['/members/edit/' + member.id]);
      return;
    }
    this.memberEditDialog = this.dialogService.open(DialogMemberFormComponent, {
      header: '', // this.translate.instant('EDITMEMBER') + ' ' + member.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        member: member,
        users: this.membersColumnsService.users,
        fitnessGoals: this.membersColumnsService.fitnessGoals,
        sports: this.membersColumnsService.sports,
      },
    });

    this.memberEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Member updated'});
        this.getMembers();
      }
    });
  }

  memberDetails(member: Member): void {
    if (!this.navigation.getParamValue('members.detailsInDialog', true)) {
      this.router.navigate(['/members/details/' + member.id]);
      return;
    }
    this.memberDetailsDialog = this.dialogService.open(DialogMemberDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        member: member,
      }
    });
  }

  memberDelete(member: Member): void {
    this.memberToDelete = member;
    this.messageService.clear();
    this.messageService.add({
      key: 'memberDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + member.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.membersDataService.deleteMember(this.memberToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Member deleted'});
      this.getMembers();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Member not deleted'});
    });
  }

  onDeleteReject(): void {
    this.memberToDelete = {};
    this.messageService.clear('memberDelete');
  }

}
